import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State } from '@stencil/core';
import * as ListManagerInterfaces from '../add-to-myti-list/add-to-myti-list.interface';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { default as languageStrings } from '../../global/l10n/CreateMytiList.json';
import { HTMLTiButtonElement, HTMLTiFormElementElement, HTMLTiInputElement, HTMLTiRadioGroupElement, InputChangeEventDetail } from '@ticom/ui-components/dist';

@Component({
    tag: 'ti-create-myti-list',
    styleUrl: 'create-myti-list.scss',
    shadow: true
})
export class CreateMytiList {

    /**
     * Add tags button reference to disable it if tagLimit reached
     */
    private _addTagsBtnRef: HTMLTiButtonElement;

    /**
     * App settings value to have settings based on user
     */
    private _appSettings: ListManagerInterfaces.AppSettings = null;

    /**
     * List of operations done when editing the list
     */
    private _editOperations: ListManagerInterfaces.AddToListOperations[] = [];

    /**
     * If edit operation is valid or not
     */
    private _editValid: boolean = false;

    /**
     * Set of existing tag names for 'edit' mode
     * NOTE: Set to avoid duplicacy of tags
     */
    private _existingTags: Set<string> = new Set<string>();

    /**
     * Reference of span element to show error if list name doesn't match valid pattern
     */
    private _invalidListNamePatternErrorRef: HTMLSpanElement;

    /**
     * List info passed as prop for edit & duplicate modes
     */
    private _listInfo: ListManagerInterfaces.ListInfo;

    /**
     * Store list-limit (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _listLimit: number = null;

    /**
     * List name input ref used to show duplicate name warning
     */
    private _listNameInputRef: HTMLTiFormElementElement;

    /**
     * Creating keys of 'ListType + ListName' to check for duplicate names
     */
    private _listTypeNameKey: Set<string> = new Set<string>();

    /**
     * For non-create mode, list type of existing list
     */
    private _listTypePrivate: boolean = true;

    /*
     * Private radio is disabled in edit mode if user
     * is not the owner of list or user is not in same company instance
     */
    private _listTypePrivateRadioEnabled: boolean = true;

    /**
     * Reference for ti-radio-group used to get list type (private or shared)
     */
    private _listTypeRef: HTMLTiRadioGroupElement;

    /**
     * Locale-specific set of language strings
     */
    private _localeStrings: Record<string, string>;

    /**
     * Mode passed as prop for edit & duplicate modes
     */
    private _mode: ListManagerInterfaces.CreateListMode;

    /**
     * In 'edit' mode, if list name is modified
     */
    private _modifiedListName: boolean = false;

    /**
     * In 'edit' mode, if list type is modified
     */
    private _modifiedListType: boolean = false;

    /**
     * In 'edit' mode, if tags have modified
     */
    private _modifiedTags: boolean = false;

    /**
     * New list name given by user
     * NOTE: used in POST api call;
     */
    private _newListName: string = null;

    /*
     * Input element reference to add tags for lists
     */
    private _tagInputElementRef: HTMLTiInputElement;

    /**
     * Store tag-limit (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _tagLimit: number = null;

    /**
     * Set of tag names to populate ti-chip element
     * NOTE: Set to avoid duplicacy of tags
     */
    private _tags: Set<string> = new Set<string>();

    /**
     * Div ref to show all tags added for list
     */
    private _tagsDivRef: HTMLDivElement;

    /**
     * Timer to debounce for list name input
     */
    private _typingTimer: NodeJS.Timeout;

    /**
     * Store user info data (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * Store user lists (if present) from prop during load, and
     * utilise it always so the we can have prop as non-mutable
     */
    private _userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Number of private lists of user
     */
    private _userOwnedPrivateListCount: number = 0;

    /**
     * Number of shared lists owned by user
     * NOTE: This includesshared list created by user for current profile
     */
    private _userOwnedSharedListCount: number = 0;

    /**
     * To store state of whether the list name is valid
     * NOTE: Empty (hence, initially invalid) and duplicate are invalid
     */
    private _validListName: boolean = false;

    @Element() hostElement: HTMLTiCreateMytiListElement;

    /**
     * Specifies progress state while data is loaded
     */
    @State() progressState: 'loading' | 'error' | 'ready' = 'loading';

    /**
     * Passed from parent component to uniquely identify events from this instance
     */
    @Prop() contextId: string = null;

    /**
     * Base url for api calls
     */
    @Prop() domain: string;

    /**
     * List info details for edit & duplicate list
     */
    @Prop() listInfo: ListManagerInterfaces.ListInfo = null;

    /**
     * List limit for user
     */
    @Prop() listLimit: number = null;

    /**
     * Action button for specifically beside list name
     */
    @Prop() listNameActionBtn: boolean = false;

    /**
     * Button text for list name action btn
     */
    @Prop() listNameActionBtnText: string = null;

    /**
     * Specifies the locale
     */
    @Prop() locale: string = 'en-US';

    /**
     * Create list mode context
     */
    @Prop() mode: ListManagerInterfaces.CreateListMode = 'create';

    /**
     * Tag limit for new list
     */
    @Prop() tagLimit: number = null;

    /**
     * User data used to have restrictions based on AppSettings
     */
    @Prop() userInfo: ListManagerInterfaces.UserInfo = null;

    /**
     * User List Info object
     *
     * NOTE: List-manager action bar instance will always use the exposed @Method
     * to update the private variable _userLists to have the latest changes.
     */
    @Prop() userLists: ListManagerInterfaces.ListInfo[] = null;

    /**
     * Event emitted when list name action button clicked
     */
    @Event() myTiListNameActionBtnEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerBasicEventDetail>;

    /**
     * Event to be emitted in case of any error for 'duplicate' mode
     */
    @Event() myTiCreateListDuplicateErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to be emitted in case of any error for 'edit' mode
     */
    @Event() myTiCreateListEditErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to be emitted in case of any error for 'create' mode
     */
    @Event() myTiCreateListErrorEvent: EventEmitter<ListManagerInterfaces.MyTiListManagerErrorEventDetail>;

    /**
     * Event to be emitted in case of list duplicated successfully
     */
    @Event() myTiCreateListDuplicateSuccessEvent: EventEmitter<ListManagerInterfaces.MyTiCreateListSuccessEventDetail>;

    /**
     * Event to be emitted in case of list edited successfully
     */
    @Event() myTiCreateListEditSuccessEvent: EventEmitter<ListManagerInterfaces.MyTiCreateListEditSuccessEventDetail>;

    /**
     * Event to be emitted in case of list created successfully
     */
    @Event() myTiCreateListSuccessEvent: EventEmitter<ListManagerInterfaces.MyTiCreateListSuccessEventDetail>;

    /**
     * Event emitted if the list name becomes valid or becomes invalid after being valid
     */
    @Event() myTiCreateListValidNameChangeEvent: EventEmitter<ListManagerInterfaces.MyTiCreateListValidNameChangeEventDetail>;

    /**
     * Event emitted if the edit action has changed any detail & is that valid
     */
    @Event() myTiCreateListEditValidChangeEvent: EventEmitter<ListManagerInterfaces.MyTiCreateListEditValidChangeEventDetail>;

    componentWillLoad() {
        // load language strings for locale - fallback to 'en-US' or an empty object
        this._localeStrings = languageStrings[this.locale] || languageStrings['en-US'] || {};

        // init data
        this._initData();
    }

    /**
     * Event listener to remove tags when user click 'X' to remove the tags
     */
    @Listen('tiChipChange')
    onTiChipChange(event: CustomEvent<{ group: string; removed?: boolean; selected: boolean; type: string; value: any; }>) {
        if (event.detail.removed) {
            this._tags.delete(event.detail.value);
            this._addTagsBtnRef.disabled = (this._tags.size >= this._tagLimit);
            if (this._mode === 'edit') {
                this._modifiedTags = this._hasTagsModified();
                this._updateListEditValidity();
            }
        }
    }

    /**
     * Method to be called manually by each application
     * to create list on click of dialog action button
     *
     * @param {ListManagerInterfaces.ListItem[]} listItems optional- list items to be added while creating new list
     */
    @Method()
    async createListAction(listItems?: ListManagerInterfaces.ListItem[]) {
        this.progressState = 'loading';

        switch (this._mode) {
            case 'create': this._onCreateListActionCreateMode(listItems);
                break;
            case 'edit': this._onCreateListActionEditMode();
                break;
            case 'duplicate': this._onCreateListActionDuplicateMode();
                break;
        }
    }

    /**
     * Method to update all lists info
     *
     * @param {ListManagerInterfaces.ListInfo[]} lists - all lists info
     */
    @Method()
    async updateAllListsInfo(lists: ListManagerInterfaces.ListInfo[]) {
        this._userLists = lists;
        this._getPrivateSharedListData();
    }

    /**
     * Method to add tags on user input
     */
    private _addTags() {
        if (this._tags.size >= this._tagLimit) {
            this._addTagsBtnRef.disabled = true;
        } else if (this._tagInputElementRef.value && !this._tags.has(this._tagInputElementRef.value)) {
            this._tags.add(this._tagInputElementRef.value);
            this._tagsDivRef.innerHTML += `<ti-chip appearance="compact" removable value="${this._tagInputElementRef.value}"></ti-chip>`;
            this._tagInputElementRef.value = '';
            this._addTagsBtnRef.disabled = (this._tags.size >= this._tagLimit);
            if (this._mode === 'edit') {
                this._modifiedTags = this._hasTagsModified();
                this._updateListEditValidity();
            }
        }
    }

    /**
     * Method to build request body and operations params for edit list API
     */
    private _buildEditListParams() {
        const requestBody: ListManagerInterfaces.ListInfo = this._listInfo;
        requestBody.modifiedBy = this._userInfo.permId.toString();  // permid of modifier
        requestBody.modifiedDate = null;                            // populated by service

        // if tags were modified in search list case, then add modify tags operation else ignore tags in service
        if (this._modifiedTags) {
            this._editOperations.push('modify-tags');
            requestBody.tags = this._getTagsInfo();
        }

        // check if list name or list type has changed
        if (this._modifiedListName || this._modifiedListType) {
            this._editOperations.push('modify-list');
            requestBody.name = this._newListName ?? this._listInfo.name;
            requestBody.sharedCpId = this._getSharedCpId();
        }

        return { requestBody, operations: this._editOperations };
    }

    /**
     * Method called to show errors in case duplicate name found for the list name
     */
    private _duplicateNameFound() {
        this._listNameInputRef.error = true;
        this._invalidListNamePatternErrorRef.style.display = 'none';
        this._updateListNameValidity(false);
    }

    /**
     * Make api call to get the app settings
     */
    private async _getAppSettings(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/settings'
            : 'data/add-to-myti-list/app-settings.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting App Settings');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.AppSettings) => {
                this._appSettings = data;
            });
        } catch (err) {
            this.myTiCreateListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
        }

        if (!this._listLimit) {
            this._listLimit = this._appSettings ? this._getListLimit() : ListManagerInterfaces.DEFAULT_LIST_LIMIT;
        }

        if (!this._tagLimit) {
            this._tagLimit = this._appSettings?.maxTagsPerList ?? ListManagerInterfaces.DEFAULT_TAG_LIMIT;
        }
    }

    /**
     * Get create list content
     */
    private _getCreateListContent() {
        if (this._mode === 'edit') {
            this._listTypePrivate = !this._listInfo.sharedCpId;
            this._listTypePrivateRadioEnabled = !this._listInfo.sharedCpId             // user's private list
                || (this._listInfo.ownerPermId === this._userInfo.permId.toString()    // user must be owner of list to change type
                && this._listInfo.sharedCpId === this._userInfo.currentCpId)           // user must have same cp id (same company instance)
        } else if ((this._userOwnedPrivateListCount + this._userOwnedSharedListCount) >= this._listLimit) {
            const errorBody: ListManagerInterfaces.MyTiListManagerErrorEventDetail = {
                contextId: this.contextId,
                errorCode: ListManagerInterfaces.CUSTOM_ERROR_STATUS_CODE_LIST_LIMIT_REACHED,
                errorMsg: 'User list creation limit reached'
            };

            if (this._mode === 'create') {
                this.myTiCreateListErrorEvent.emit(errorBody);
            } else if (this._mode === 'duplicate') {
                this.myTiCreateListDuplicateErrorEvent.emit(errorBody);
            }
            return <div>
                {this._localeStrings['listLimitErrorText'].replace('{0}', this._listLimit.toString())}
            </div>;
        }

        return <div>
            <div class="ti-create-myti-list-listname-container">
                <ti-form-element
                    label-text={this._localeStrings['listNameLabel']}
                    required
                    ref={(el) => this._listNameInputRef = el}>
                    <ti-input
                        value={this._mode === 'edit' ? this._listInfo.name : ''}
                        placeholder={this._localeStrings['listNameInputPlaceholder']}
                        onTiChange={(ev: CustomEvent<InputChangeEventDetail>) => this._listNameChanged(ev)}>
                    </ti-input>
                    {this.listNameActionBtn &&
                        <ti-button
                            appearance='link'
                            onClick={() => this.myTiListNameActionBtnEvent.emit({contextId: this.contextId})} class="ti-create-myti-list-listname-btn">
                            {this.listNameActionBtnText}
                        </ti-button>
                    }
                    <span slot="error" class="ti-create-myti-list-listname-invalid-error">{this._localeStrings['duplicateListNameErrorText']}</span>
                    <span
                        class="ti-create-myti-list-listname-invalid-error"
                        style={{ display: 'none' }}
                        ref={(el) => this._invalidListNamePatternErrorRef = el}>
                        {this._localeStrings['invalidListNameErrorText']}
                    </span>
                </ti-form-element>

            </div>
            <p class="ti-create-myti-list-label-count">{ this._localeStrings['existingListLimitInfoText'].replace('{0}',(this._userOwnedPrivateListCount + this._userOwnedSharedListCount).toString()).replace('{1}', this._listLimit.toString())}</p>

            <ti-form-element
                label-text={this._localeStrings['tagsLabel']}
                class="ti-create-myti-list-form-row">
                <ti-input
                    class="ti-create-myti-list-form"
                    placeholder={this._localeStrings['tagInputPlaceholder']}
                    ref={(el) => this._tagInputElementRef = el}
                    onKeyDown={(e) => {
                        if (e.code === 'Enter') {
                            this._addTags();
                        }
                    }} />
                <ti-button appearance='compact secondary'
                    onClick={() => this._addTags()}
                    innerHTML={this._localeStrings['addTagsBtnText']}
                    class="ti-create-myti-list-add-tag-btn"
                    disabled={this._tags.size >= this._tagLimit}
                    ref={(el) => this._addTagsBtnRef = el}></ti-button>
            </ti-form-element>

            <div class="ti-create-myti-list-tags" ref={(el) => this._tagsDivRef = el}>{this._renderExistingTags()}</div>

            <ti-form-element
                label-text={this._localeStrings['listTypeLabel']}>
                <ti-radio-group horizontal ref={(el) => this._listTypeRef = el} onClick={() => this._listTypeChanged()}>
                    <ti-radio value={ListManagerInterfaces.LIST_TYPE_PRIVATE} selected={this._listTypePrivate} disabled={!this._listTypePrivateRadioEnabled}>{this._localeStrings['listTypePrivateText']}</ti-radio>
                    <ti-radio value={ListManagerInterfaces.LIST_TYPE_SHARED} selected={!this._listTypePrivate} disabled={!this._userInfo.currentCpId}>{this._localeStrings['listTypeSharedText']}</ti-radio>
                </ti-radio-group>
            </ti-form-element>
        </div>;
    }

    /**
     * Method to get request body for create list & duplicate list POST api calls
     */
    private _getCreateListRequestBody(listItems?: ListManagerInterfaces.ListItem[]) {
        const newListInfo: ListManagerInterfaces.ListInfo = {
            listId: null,               // will be generated by DB
            name: this._newListName,    // given by user
            type: null,                 // populated by service
            quantityMultiplier: 1,      // default, for duplicate mode will be picked up from DB
            createdDate: null,          // to be populated by service as JS Date & Java Date interfaces are diff
            modifiedDate: null,         // since new list, always null
            modifiedBy: null,           // since new list, always null
            ownerPermId: this._userInfo.permId.toString(),  // userPermId, will be re-validated & then populated by service
            ownerName: null,            // to be populated by service after validation
            sharedCpId: this._getSharedCpId(),  // activeCpId, will be re-validated & then populated by service
            items: listItems ?? [],
            totalItemCount: listItems?.length ?? null,  // dynamically calculated & updated by service
            maxItemLimit: null,         // needs to be checked in the service based on user & populate it
            nextPageItemIndex: null,
            tags: this._getTagsInfo(),
            salesConsent: null
        };
        return newListInfo;
    }

    /**
     * Return error content
     */
    private _getErrorContent() {
        let errorMsg: string;
        switch (this._mode) {
            case 'create': errorMsg = this._localeStrings['createListErrorText'];
                break;
            case 'edit': errorMsg = this._localeStrings['createListEditErrorText'];
                break;
            case 'duplicate': errorMsg = this._localeStrings['createListDuplicateErrorText'];
                break;
            default: errorMsg = this._localeStrings['createListDefaultErrorText'];
        }
        return <div><p>{errorMsg}</p></div>;
    }

    /**
     * Return the list limit from App Settings based on userType
     */
    private _getListLimit(): number {
        if (this._userInfo.isCGX) {
            return this._appSettings.maxListPerCGX;
        } else {
            switch (this._userInfo.userType) {
                case 'TIER': return this._appSettings.maxListPerTIer;
                case 'DIRECT': return this._appSettings.maxListPerDirect;
                case 'MASSMARKET': return this._appSettings.maxListPerMassMarket;
                default: return ListManagerInterfaces.DEFAULT_LIST_LIMIT;
            }
        }
    }

    /**
     * Return the loading spinner
     */
    private _getLoadingContent() {
        return [
            <div class="ti-create-myti-dialog-loader-container">
            <ti-progress></ti-progress>
        </div>
        ];
    }

    /**
     * Based on users all list, get private & shared list info
     */
    private async _getPrivateSharedListData() {
        this._listTypeNameKey.clear();
        this._userOwnedPrivateListCount = 0;
        this._userOwnedSharedListCount = 0;

        await this._userLists.forEach((list: ListManagerInterfaces.ListInfo) => {
            if (!list.sharedCpId) {
                this._listTypeNameKey.add(ListManagerInterfaces.LIST_TYPE_PRIVATE + list.name);
                if (list.ownerPermId === this._userInfo.permId.toString()) {
                    this._userOwnedPrivateListCount++;
                }
            } else if (list.sharedCpId === this._userInfo.currentCpId) {
                this._listTypeNameKey.add(ListManagerInterfaces.LIST_TYPE_SHARED + list.name);
                if (list.ownerPermId === this._userInfo.permId.toString()) {
                    this._userOwnedSharedListCount++;
                }
            }
        });
    }

    /**
     * Get shared cpId for listInfo based on radio btn selected
     */
    private _getSharedCpId(): number {
        return this._listTypeRef.value === ListManagerInterfaces.LIST_TYPE_SHARED
            ? this._userInfo.currentCpId
            : null;
    }

    /**
     * Method to get tags info for POST api call
     */
    private _getTagsInfo() {
        const tagInfo: ListManagerInterfaces.TagInfo[] = [];
        this._tags.forEach((tag: string) => {
            tagInfo.push({
                name: tag,
                tagId: null,
                listId: null
            });
        });

        return tagInfo;
    }

    /**
     * Make api call to get user info
     */
    private async _getUserInfo() {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/user'
            : 'data/add-to-myti-list/user-info.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Info');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.UserInfo) => {
                this._userInfo = data;
            });
        } catch (err) {
            this.myTiCreateListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
            this.progressState = 'error';
        }
    }

    /**
     * Make api call to get all the list infos for the user
     */
    private async _getUserList(): Promise<void> {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/list'
            : 'data/add-to-myti-list/all-lists.json';

        let errorResponseStatus: number = null;
        try {
            await this._makeApiCall(apiUrl, 'GET', null).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in getting User Lists');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo[]) => {
                this._userLists = data;
            });
        } catch (err) {
            this.myTiCreateListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
            this.progressState = 'error';
        }
    }

    /**
     * Method to check if tags have been modified
     */
    private _hasTagsModified(): boolean {
        if (this._existingTags.size !== this._tags.size) {
            return true;
        }
        for (const tagName of this._tags) {
            if (!this._existingTags.has(tagName)) {
                return true;
            }
        }
        return false;
    }

    /**
     * To make the required api calls and get the necessary data
     */
    private async _initData() {
        this._initProps();
        if (this._mode !== 'create' && !this._listInfo) {
            if (this._mode === 'edit') {
                this.myTiCreateListEditErrorEvent.emit({
                    contextId: this.contextId,
                    errorCode: ListManagerInterfaces.CUSTOM_ERROR_STATUS_CODE_LIST_INFO_NOT_AVAILABLE,
                    errorMsg: 'ListInfo not available to edit'
                });
            } else if (this._mode === 'duplicate') {
                this.myTiCreateListDuplicateErrorEvent.emit({
                    contextId: this.contextId,
                    errorCode: ListManagerInterfaces.CUSTOM_ERROR_STATUS_CODE_LIST_INFO_NOT_AVAILABLE,
                    errorMsg: 'ListInfo not available to duplicate'
                });
            }
            this.progressState = 'error';
        }

        // if userInfo prop is null, then make-api call to get the info
        if (!this._userInfo) {
            await this._getUserInfo();
        }

        if (this.progressState !== 'error') {
            let promise1: Promise<void>;
            let promise2: Promise<void>;

            // if userLists prop is null, then make the api-call to get the info
            if (!this._userLists) {
                promise1 = this._getUserList();
            }

            // if listLimit or tagLimit prop is null, then make the api call to get the appSettings
            if ((!this._listLimit || !this._tagLimit)) {
                promise2 = this._getAppSettings();
            }

            await Promise.all([promise1, promise2]);

            // if there is no error, then the component is ready to be rendered
            if (this.progressState === 'loading') {
                await this._getPrivateSharedListData();

                // for non 'create' mode, get tag values
                if (this._mode !== 'create') {
                    this._tags = new Set(this._listInfo.tags.map((tag: ListManagerInterfaces.TagInfo) => tag.name));
                    this._existingTags = new Set(this._tags);
                    // to mark the existing list name as valid for 'edit' & 'duplicate' modes
                    this._validListName = true;
                }

                this.progressState = 'ready';
            }
        }
    }

    /**
     * Method to get get data from props, and
     * set it to private variables to avoid data manipulation from client-side
     */
    private _initProps() {
        this._listInfo = this.listInfo;
        this._listLimit = this.listLimit;
        this._mode = this.mode;
        this._tagLimit = this.tagLimit;
        this._userInfo = this.userInfo;
        this._userLists = this.userLists;
    }

    /**
     * Method called when list name is changed
     */
    private _listNameChanged(ev: CustomEvent<InputChangeEventDetail>) {
        // to remove leading & trailing spaces
        this._newListName = ev.detail.value?.trim();
        clearTimeout(this._typingTimer);
        this._typingTimer = setTimeout(() => this._validateListName(), ListManagerInterfaces.CREATE_LIST_NAME_TYPING_DEBOUNCE);
    }

    /**
     * Method called when list type is changed (private or shared)
     */
    private _listTypeChanged() {
        if (this._newListName) {
            this._validateListName();
        }
        if (this._mode === 'edit') {
            this._modifiedListType = this._listInfo?.sharedCpId !== this._getSharedCpId();
            this._updateListEditValidity();
        }
    }

    /**
     * Generic method to make all api calls
     */
    private async _makeApiCall(apiUrl: string,
        methodType: ListManagerInterfaces.HttpMethodType,
        requestBody: any,
        timeout: number = ListManagerInterfaces.LIST_API_TIMEOUT_VALUE
    ): Promise<Response> {
        const apiResponse: Response = await fetchWithTimeout(apiUrl,
            {
                method: methodType,
                body: requestBody !== null && requestBody !== undefined ? JSON.stringify(requestBody) : null,
                credentials: 'include',
                headers: {
                    'Cache-Control': 'no-store, must-revalidate',
                    'Expires': '0',
                    'Content-Type': 'application/json'
                }
            }, timeout);

        return apiResponse;
    }

    /**
     * Method called when dialog action btn is clicked for 'create' mode
     */
    private async _onCreateListActionCreateMode(listItems: ListManagerInterfaces.ListItem[]) {
        const apiUrl: string = this.domain
            ? this.domain + '/listmodel/api/v1/ext/secured/list'
            : 'data/add-to-myti-list/create-list.json';

        const createListRequestBody: ListManagerInterfaces.ListInfo = await this._getCreateListRequestBody(listItems);
        let errorResponseStatus: number = null;
        let createdList: ListManagerInterfaces.ListInfo = null;
        try {
            await this._makeApiCall(apiUrl, 'POST', createListRequestBody).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in creating new List');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                createdList = data;
            });
        } catch (err) {
            this.myTiCreateListErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
            this.progressState = 'error';
        }

        if (this.progressState !== 'error') {
            this.myTiCreateListSuccessEvent.emit({
                contextId: this.contextId,
                createdList
            });
        }
    }

    /**
     * Method called when dialog action btn is clicked for 'duplicate' mode
     */
    private async _onCreateListActionDuplicateMode() {
        const duplicateListRequestBody: ListManagerInterfaces.ListInfo = this._getCreateListRequestBody();

        // the actual API implementation
        const apiUrl: string = this.domain
            ? this.domain + `/listmodel/api/v1/ext/secured/list/${this._listInfo.listId}/copy`
            : 'data/add-to-myti-list/create-list.json';

        let errorResponseStatus: number = null;
        let createdList: ListManagerInterfaces.ListInfo = null;
        try {
            await this._makeApiCall(apiUrl, 'POST', duplicateListRequestBody).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in duplicating list');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                createdList = data;
            });
        } catch (err) {
            this.myTiCreateListDuplicateErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
            this.progressState = 'error';
        }

        if (this.progressState !== 'error') {
            this.myTiCreateListDuplicateSuccessEvent.emit({
                contextId: this.contextId,
                createdList
            });
        }
    }

    /**
     * Method called when dialog action btn is clicked for 'edit' mode
     */
    private async _onCreateListActionEditMode() {
        const editListParams = this._buildEditListParams();

        // the actual API implementation
        const apiUrl: string = this.domain
            ? this.domain + `/listmodel/api/v1/ext/secured/list/${this._listInfo.listId}?operations=${editListParams.operations.join()}`
            : 'data/add-to-myti-list/edit-list.json';

        let errorResponseStatus: number = null;
        let editedList: ListManagerInterfaces.ListInfo = null;
        try {
            await this._makeApiCall(apiUrl, 'PATCH', editListParams.requestBody).then((apiResponse: Response) => {
                if (!apiResponse.ok) {
                    errorResponseStatus = apiResponse.status;
                    throw new Error('Error in editing list');
                }
                return apiResponse.json();
            }).then((data: ListManagerInterfaces.ListInfo) => {
                editedList = data;
                if (data.errors?.length) {
                    throw new Error('Error in editing items or tags');
                }
            });
        } catch (err) {
            this.myTiCreateListEditErrorEvent.emit({ contextId: this.contextId, errorCode: errorResponseStatus, errorMsg: err.message });
            this.progressState = 'error';
        }

        if (this.progressState !== 'error') {
            this.myTiCreateListEditSuccessEvent.emit({
                contextId: this.contextId,
                editedList
            });
        }
    }

    /**
     * For 'edit' & 'duplicate' modes, render if there are existing tags
     * NOTE: in case there are more tags than the limit, we remove extra tags
     */
    private _renderExistingTags() {
        return Array.from(this._tags).slice(0, this._tagLimit).map((tagVal: string) =>
            <ti-chip appearance="compact" removable value={tagVal}></ti-chip>
        );
    }

    /**
     * Check if list edit validity has changed,
     * if yes, update the value & then emit event for it.
     */
    private _updateListEditValidity() {
        const newValue: boolean = this._validListName && (this._modifiedListName || this._modifiedListType || this._modifiedTags);
        if (this._editValid !== newValue) {
            this._editValid = newValue;
            this.myTiCreateListEditValidChangeEvent.emit({ contextId: this.contextId, isEditValid: this._editValid });
        }
    }

    /**
     * Check if list name validity has changed,
     * if yes, update the value & then emit event for it.
     */
    private _updateListNameValidity(newValue: boolean) {
        if (this._validListName !== newValue) {
            this._validListName = newValue;
        }
        switch (this._mode) {
            case 'create':
            case 'duplicate': this.myTiCreateListValidNameChangeEvent.emit({ contextId: this.contextId, isListNameValid: this._validListName });
                break;
            case 'edit': this._updateListEditValidity();
                break;
        }
    }

    /**
     * Method to validate the list name for duplicate or valid list name regex patterm
     */
    private _validateListName() {
        const newListTypeNameKey: string = this._listTypeRef.value + this._newListName;
        let existingListTypeNameKey: string;
        if (this._mode === 'edit') {
            const existingListType: string = this._listInfo.sharedCpId ? ListManagerInterfaces.LIST_TYPE_SHARED : ListManagerInterfaces.LIST_TYPE_PRIVATE;
            existingListTypeNameKey = existingListType + this._listInfo.name;
        }

        // regex check, i.e, only 30 alphanumeric, spaces, underscores & dashes allowed
        // also list name can't be empty
        if (!new RegExp(ListManagerInterfaces.VALID_LIST_NAME_PATTERN).test(this._newListName) || (this._newListName.length >= ListManagerInterfaces.LIST_NAME_CHAR_LIMIT)) {
            this._invalidListNamePatternErrorRef.style.display = 'block';
            this._listNameInputRef.error = false;
            this._updateListNameValidity(false);
        }
        // duplicate list name check for lists for non 'edit' mode
        else if (this._mode !== 'edit' && this._listTypeNameKey.has(newListTypeNameKey)) {
            this._duplicateNameFound();
        }
        // duplicate list name check for lists for 'edit' mode
        // need to exclude check for already existing list which is being edited
        else if (this._mode === 'edit' && existingListTypeNameKey !== newListTypeNameKey && this._listTypeNameKey.has(newListTypeNameKey)) {
            this._duplicateNameFound();
        }
        // valid list name
        else {
            // in 'edit' mode, check if list name has been modified
            if (this._mode === 'edit') {
                this._modifiedListName = this._newListName !== this._listInfo.name;
            }
            this._listNameInputRef.error = false;
            this._invalidListNamePatternErrorRef.style.display = 'none';
            this._updateListNameValidity(true);
        }
    }

    /**
     * Render the component based on progressStsate
     */
    render() {
        switch (this.progressState) {
            case 'ready': return <Host>{this._getCreateListContent()}</Host>;
            case 'loading': return <Host>{this._getLoadingContent()}</Host>;
            case 'error': return <Host>{this._getErrorContent()}</Host>;
        }
    }
}
